<template>
  <div>
    {{$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.optionsEditableLater')}}
    <v-divider class="mt-4 mb-6"/>
    <v-row>
      <v-col>
        {{$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.text')}}
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="pb-0">
        <AppTextField v-model.trim="validator.name.$model"
                      class="required"
                      :label="$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.name')"
                      :error-messages="roomNameError"
                      maxLength="100"
                      counter="100"
        />
      </v-col>
    </v-row>
    <v-row>
      <ProjectsMetadataSelectGroup :room="newDataRoom"/>

      <v-col cols="12" md="6" class="mb-2">
        <template v-if="subscriptionLabelsPending">
          <v-skeleton-loader type="text"/>
        </template>
        <template v-else>
          <AppSelect v-if="computedRoomsLabels"
                      v-model="newDataRoom.labelId"
                      :items="computedRoomsLabels"
                      :label="$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.status')"
                      item-title="text"
                      item-value="id"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <template #title>
                  <div v-if="item.raw.id === null">
                    {{ item.raw.text }}
                  </div>
                  <AppProjectLabel v-else :label="item.raw.label"/>
                </template>
              </v-list-item>
            </template>
            <template #selection="{ item }">
              <div v-if="item.raw.id === null">
                {{ item.raw.text }}
              </div>
              <AppProjectLabel v-else :label="item.raw.label"/>
            </template>
          </AppSelect>
        </template>
      </v-col>

      <v-col cols="12" md="6">
        <AppDatePicker ref="picker"
                        v-model="newDataRoom.expClosingDate"
                        :label="$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.plannedCompletionDate')"
                        class="w-100"
                        min="2000-01-01"
        />
      </v-col>
      <v-col cols="12" md="6" class="pb-0">
        <AppTextField v-model="newDataRoom.cmRef"
                      :label="$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.cmRef')"
                      maxLength="100"
                      counter="100"
        />
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <p class="mb-2 text--medium text--bold text-text-primary">
      {{ $t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.options') }}
    </p>
    <div class="d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <span class="text--small text--bold text-text-secondary">
          {{ $t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.displayWatermark') }}
        </span>
        <AppFeatureTag tag="new" class="ml-2" />
      </div>
      <app-toggle
        v-model="newDataRoom.displayWatermark"
        class="mt-n1"
        density="compact"
        hide-details
      />
    </div>
    <div class="text--caption text-text-tertiary">
      <div>{{ $t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.displayWatermarkDescription1') }}</div>
      <div>{{ $t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.displayWatermarkDescription2') }}</div>
    </div>

    <v-divider class="mt-6"></v-divider>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import AppDatePicker from '@/common/AppDatePicker.vue'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppProjectLabel from '@/common/AppProjectLabel.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { GET_SUBSCRIPTION_LABELS } from '@/store/modules/rooms/action_types'

import ProjectsMetadataSelectGroup from './ProjectsMetadataSelectGroup.vue'

export default {
  name: 'ProjectsNewDataroomDialogStep1',
  components: {
    AppSelect,
    AppTextField,
    AppDatePicker,
    AppProjectLabel,
    AppFeatureTag,
    ProjectsMetadataSelectGroup,
  },
  props: {
    newDataRoom: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('rooms', ['subscriptionLabels', 'subscriptionLabelsPending']),
    computedRoomsLabels () {
      const labels = []
      labels.push({
        id: null,
        text: this.$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.noLabel'),
      })
      if (this.subscriptionLabels) {
        labels.push(
          ...this.subscriptionLabels.map(
              subscriptionLabel => ({
                id: subscriptionLabel.id,
                text: subscriptionLabel.label[this.$i18n.locale],
                label: {
                  label: subscriptionLabel.label,
                  color: subscriptionLabel.color,
                },
              }),
            ),
        )
      }
      return labels
    },
    roomNameError() {
      return formFieldErrors(
        this.validator.name,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.name') })],
          ['maxLength', this.$t('common.validations.maxLength', { fieldName: this.$t('common.validations.maxLength', { fieldName: this.$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.fieldsLabels.name') }) })],
        ],
      )
    },
  },
  async mounted () {
    try {
      await this.GET_SUBSCRIPTION_LABELS(
        {
          params: {
            isDataroom: true,
          },
        },
      )
    } catch (e) {
      toast.error(this.$t('projects.dialogs.ProjectsNewDataroomDialogCreateDataRoom.getRoomsLabelsError'))
    }
  },
  methods: {
    ...mapActions('rooms', [GET_SUBSCRIPTION_LABELS]),
  },
}
</script>
